import * as React from 'react';
import {Column, isNavigationKey} from '../components/BasicDataGrid';
import ABaseColumn, {IColumnDefinition, IBaseColumnProps, IBaseColumnState, Filter} from './BaseColumn';
import {FlagNameValues, Icon, SemanticICONS} from 'semantic-ui-react';

interface IMultiLineColumn {
}

export interface MultiLineColumnPropsDefinition extends Column, IMultiLineColumn {

}

interface IMultiLineColumnPropsDefinition extends IColumnDefinition, IMultiLineColumn {

}

interface IMultiLineColumnProps extends IBaseColumnProps {
    column?: IMultiLineColumnPropsDefinition;
}

/**
 * Get the FlagCode for a given language code (LanguageDto.code).
 *
 * This method will no longer be neccessary when there's country code info
 * in the DB because the country code is equivalent to the flag code.
 *
 * @param langCode the language code
 * @returns the flag code that corresponds to the language code or undefined if none was found
 */
export const lang2flag = (vmce: string): FlagNameValues => {
    let flag: FlagNameValues = '' as FlagNameValues;
    switch (vmce) {
        case "de":
        case "DE":
        case "sd":
            flag = "de";
            break;
        case "cc":
        case "se":
        case "en":
        case "bc":
            flag = "gb";
            break;
        case "ae":
            flag = "au";
            break;
        case "da":
            flag = "dk";
            break;
        case "fi":
            flag = "fi";
            break;
        case "it":
        case "si":
            flag = "italy";
            break;
        case "nl":
            flag = "netherlands";
            break;
        case "sp":
        case "ls":
            flag = "spain";
            break;
        case "po":
            flag = "portugal";
            break;
        case "pj":
            flag = "japan";
            break;
        case "us":
            flag = "united states";
            break;
        case "sv":
            flag = "sweden";
            break;
        case "cf":
        case "sf":
        case "fr":
        case "bf":
            flag = "france";
            break;
        case "ru":
            flag = "russia";
            break;
        case "cn":
            flag = "china";
            break;
        case "hk":
            flag = "hong kong";
            break;
        case "pl":
            flag = "poland";
            break;
        case "sg":
            flag = "singapore";
            break;
        case "kr":
            flag = "kr";
            break;
        case "br":
            flag = "brazil";
            break;
        case "tw":
            flag = "taiwan";
            break;
        default:
            break;
    }
    return flag;
};

/**
 * Interface for column items
 */
export interface IMultiLineValue {
    // Text that renders before each text field.
    caption?: string;
    // Flag icon displayed before the normal icon.
    flag?: FlagNameValues;
    // Icon (and optional hovering text) which renders in the same line before the caption.
    icon?: {name: SemanticICONS, text?: string};
    // Text itself that will be rendered inside a text area and which can
    // be editable.
    text: string;
    // Optional text for comparing
    originalText?: string;
}

interface IMultiLineColumnState extends IBaseColumnState {
    value: IMultiLineValue[];
}

/**
 * @author bomaier
 */
export default class MultiLineColumn extends ABaseColumn<IMultiLineColumnProps, IMultiLineColumnState> {

    /**
     * Ignore what's passed through the props.value variable because it may
     * or may not be a IMultiLineValue[]. Instead access the row value for
     * this column directly and save it in this variable untils it's used.
     */
    private innerValues: IMultiLineValue[] = [];

    /**
     * Save the last selected/focused text field index.
     * The index is used to focus the correct input field.
     * This property has to be static because MultiLineColum instances
     * are created and destroyed in between selection of text fields.
     */
    private static domIdx: number | undefined = undefined;

    /**
     * Save the DOM nodes of each text field input element.
     * The one that corresponds to domIdx will receive the focus.
     */
    private domNodes: HTMLTextAreaElement[] | null[] = [];

    constructor (props: IMultiLineColumnProps) {
        super(props);

        this.change = this.change.bind(this);
    }

    componentWillReceiveProps(nextProps: Readonly<IMultiLineColumnProps>): void {
        // HACK: clear the saved innerValues in case we receive new valid.
        // values. innerValues will be updated next time render() is called.
        if (Array.isArray(nextProps.value) && nextProps.value !== this.props.value) {
            this.innerValues = [];
            this.domNodes = [];
        }
        super.componentWillReceiveProps(nextProps);
    }

    /**
     * Changes the value of a IMultiLineValue object from the column array of
     * IMultiLineVaue items.
     *
     * @param index
     *      number, the index of the IMultiLineValue object from the array that should
     *      be changed.
     * @param value
     *      IMultiLineValue, the new value
     */
    change (index: number, value: string) {
        if (this.innerValues.length > index) {
            // update the initially saved line state and pass it on.
            this.innerValues[index] = Object.assign({}, this.innerValues[index], {text: value});
            this.onChange(this.innerValues);
        }
    }

    /**
     * Renders a section for each IMultiLineValue within a IMultiLneValue array
     *
     * @param content
     *      Array of IMultiLineValue
     * @param editMode
     *      Boolean, true if text area for each text can be edited, false otherwise.
     */
    renderTextSectionEditMode = (content: IMultiLineValue[], editMode: boolean) => {

        const returnValue = content.map((obj, index) => {
            const ta = (
                <textarea
                    className="MultiLineColumnLine"
                    ref={ref => {
                        if (ref) {
                            // store the input field DOM nodes of the current column instance
                            this.domNodes[index] = ref;
                        }
                        const domNodeIdx = MultiLineColumn.domIdx;
                        if (domNodeIdx !== null && domNodeIdx === index) {
                            const domNode = this.domNodes[domNodeIdx];
                            if (domNode) {
                                // select the input field DOM if it was the
                                // last one that received the focus
                                this.setInputNode(domNode);
                            }
                        }
                    }}
                    readOnly={!editMode}
                    value={obj.text}
                    rows={6}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                        this.change(index, event.target.value);
                    }}
                    onFocus={(event: React.FocusEvent<HTMLTextAreaElement>): void => {
                        MultiLineColumn.domIdx = index;
                    }}
                    onBlur={(event: React.FocusEvent<HTMLTextAreaElement>): void => {
                        MultiLineColumn.domIdx = undefined;
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
                        if (isNavigationKey(event.key))
                            event.stopPropagation();
                    }}
                    onKeyUp={(event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
                        if (isNavigationKey(event.key))
                            event.stopPropagation();
                    }}
                    onKeyPress={(event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
                        if (isNavigationKey(event.key))
                            event.stopPropagation();
                    }}
                />
            );

            return (
                <div key={this.getId() + "_" + index}>
                    <div className="MultiLineColumnCaption">
                        {obj.flag !== undefined ? <i className={obj.flag + " flag"}/> : <i/>}
                        {obj.icon ? <Icon name={obj.icon.name} title={obj.icon.text} /> : <i />}
                        {obj.caption}
                    </div>
                    <div>
                        {ta}
                    </div>
                </div>
            );
        });

        return (
            <div className='section'>
                {returnValue}
            </div>
        );
    }

    /**
     * Renders the custom column.
     * @param editMode
     *      true if the text areas within the column are editable, false otherwise.
     */
    renderCustom(editMode: boolean): JSX.Element {

        // Initialize innerValues as soon as ABaseColumn has initialized the state.
        if (this.innerValues.length === 0 && this.state.column && this.state.rowData) {
            // Take a copy of the original text values so we can modify each
            // text line before the data grid sends an onChanged() event.
            // If we don't do this only the last modification is reported.
            this.innerValues = Object.assign([], this.state.rowData[this.state.column.key]);
        }

        const _content = this.renderTextSectionEditMode(this.innerValues, editMode);

        return (
            <div className='multi-line-column'>
                {_content}
            </div>
        );
    }

    /**
     * Apply the filter to each multi line value to check if at least one match is found.
     * See @ABaseColumn.filterValid() for more details.
     */
    static filterValid(filter: Filter, rowData: any): boolean {
        const rowValue = rowData[filter.column.key];
        if (rowValue === undefined || !Array.isArray(rowValue)) {
            return false;
        }
        const multiLineValues = rowValue as IMultiLineValue[];

        const value: string = multiLineValues.map(line => line.text).join(" ").toLowerCase();
        const filterGroups: string[] = filter.filterTerm.toString().trim().toLowerCase().split("|");
        return ABaseColumn.isValidFilterValue(filterGroups, value, filter.column.exactFiltering || false);
    }
}



// WEBPACK FOOTER //
// ./src/components/MultiLineColumn.tsx